<template>
  <section
    v-if="pointAchievement"
    class="p-the-h01-point-achievement-panel"
    aria-label="ポイント実績"
  >
    <div class="p-the-h01-point-achievement-panel__items">
      <div class="p-the-h01-point-achievement-panel__item">
        <div class="p-the-h01-point-achievement-panel__label">
          <IconBase
            class="p-the-h01-point-achievement-panel__icon"
            view-box="0 0 40 40"
            :width="32"
            :height="32"
          >
            <IconInvested :icon-color="colorCode" />
          </IconBase>
          <span>付与されたポイント</span>
        </div>
        <div
          class="p-the-h01-point-achievement-panel__point"
          data-translate-skip
        >
          <span class="p-the-h01-point-achievement-panel__point__value">
            {{ addComma(cumulativeGrantedPoints) }}
          </span>
          <span class="p-the-h01-point-achievement-panel__point__unit">
            ポイント
          </span>
        </div>
      </div>

      <div
        v-if="thanksPointEnabled && isGroupBudget === false"
        class="p-the-h01-point-achievement-panel__item"
      >
        <div class="p-the-h01-point-achievement-panel__label">
          <IconBase
            class="p-the-h01-point-achievement-panel__icon"
            view-box="0 0 40 40"
            :width="32"
            :height="32"
          >
            <IconGive :icon-color="colorCode" />
          </IconBase>
          <span>贈った{{ thanksPointName }}</span>
        </div>
        <div
          class="p-the-h01-point-achievement-panel__point"
          data-translate-skip
        >
          <span class="p-the-h01-point-achievement-panel__point__value">
            {{ addComma(pointAchievement.cumulativePointsToSend) }}
          </span>
          <span class="p-the-h01-point-achievement-panel__point__unit">
            {{ pointName }}
          </span>
        </div>
      </div>

      <div
        v-if="thanksPointEnabled && isGroupBudget === false"
        class="p-the-h01-point-achievement-panel__item"
      >
        <div class="p-the-h01-point-achievement-panel__label">
          <IconBase
            class="p-the-h01-point-achievement-panel__icon"
            view-box="0 0 40 40"
            :width="32"
            :height="32"
          >
            <IconGiven :icon-color="colorCode" />
          </IconBase>
          <span>受け取った{{ thanksPointName }}</span>
        </div>
        <div
          class="p-the-h01-point-achievement-panel__point"
          data-translate-skip
        >
          <span class="p-the-h01-point-achievement-panel__point__value">
            {{ addComma(pointAchievement.cumulativeThanksPoints) }}
          </span>
          <span class="p-the-h01-point-achievement-panel__point__unit">
            {{ pointName }}
          </span>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconBase from "@/common/components/atoms/IconBase.vue";
import IconGiven from "@/common/components/icons/IconGiven.vue";
import IconGive from "@/common/components/icons/IconGive.vue";
import IconInvested from "@/common/components/icons/IconInvested.vue";
import { addComma as addCommaFilter } from "@/common/assets/js/filters";
import { getThanksPointName } from "@/common/assets/js/utils/pointName";
import commons from "@/common/store/modules/commons";
import TopPageApi from "@/general/api/topPageApi";
import CONST_DATA from "@/general/mixins/const/constData";
import { UserPointAchievement } from "@/general/store/models";

@Options({
  name: "TheH01PointAchievementPanel",
  components: {
    IconBase,
    IconGiven,
    IconGive,
    IconInvested
  }
})
export default class TheH01PointAchievementPanel extends Vue {
  pointAchievement: UserPointAchievement | null = null;

  get isGroupBudget() {
    return commons?.userInfo.groupBudgetMode;
  }

  get colorCode() {
    if (this.isGroupBudget) {
      return "#fc9014";
    }
    return commons.colorCode;
  }

  get cumulativeGrantedPoints() {
    if (!this.pointAchievement) {
      return 0;
    }
    if (this.isGroupBudget) {
      return this.pointAchievement.totalCumulativeGroupBudgetPoints;
    }
    return this.pointAchievement.totalCumulativePoints;
  }

  get thanksPointName() {
    return getThanksPointName(CONST_DATA.POINT_NAME.THANKS_POINT_DEFAULT_NAME2);
  }

  get pointName() {
    return commons.OrgPageSpecific?.point_name || commons?.unit?.point;
  }

  get thanksPointEnabled() {
    return commons.thanksFunctionEnabled;
  }

  async created() {
    this.pointAchievement = await TopPageApi.getUserPointAchievement();
  }

  addComma = addCommaFilter;
}
</script>

<style lang="scss" scoped>
.p-the-h01-point-achievement-panel {
  box-sizing: border-box;
  width: 100%;
  padding: rem(16);
  background: $common-white;
  border: px(2) solid #f4f5f6;
  border-radius: rem(8);
  box-shadow: 0 0 rem(4) rgba(0, 0, 0, 0.1);
  color: $common-black;
  font-family: "Noto Sans JP", sans-serif;
  font-weight: 700;

  &__items {
    display: flex;
    flex-direction: column;
    gap: rem(16);

    @include large-pc {
      flex-direction: row;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: rem(8);
    min-width: 0;

    @include large-pc {
      flex: 1 1 0;
      flex-direction: column;
      justify-content: center;
      gap: rem(4);
    }

    @include large-sp {
      & + & {
        padding-top: rem(16);
        border-top: px(1) solid #dcdee0;
      }
    }
  }

  &__label {
    display: flex;
    flex: 1 1 auto;
    align-items: center;
    gap: rem(4);
    min-width: 0;
    font-size: rem(14);
    line-height: 1.25;
    font-feature-settings: "pwid" 1, "palt" 1;

    > span {
      overflow-wrap: anywhere;
    }

    @include large-pc {
      flex: 0 1 auto;
      justify-content: center;
      text-align: left;
    }
  }

  &__icon {
    flex: 0 0 rem(32);
    color: $common-white;
  }

  &__point {
    display: flex;
    flex: 0 0 auto;
    align-items: flex-end;
    gap: rem(2);
    line-height: 1;
    white-space: nowrap;

    &__value {
      font-size: rem(18);
    }

    &__unit {
      font-size: rem(12);
    }
  }
}
</style>
